import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatNativeDateModule,
  MAT_DATE_FORMATS,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatFormFieldModule,
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxMaskDirective } from 'ngx-mask';
import { formatacao_data_br } from '../form-field/form-field.component';

@Component({
  selector: 'app-form-textarea',
  templateUrl: './form-textarea.component.html',
  styleUrls: ['./form-textarea.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    NgxMaskDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckbox,
    MatTooltip,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: MAT_DATE_FORMATS, useValue: formatacao_data_br },
    provideNativeDateAdapter(),
  ],
})
export class FormTextareaComponent implements AfterViewInit, OnChanges {
  @Input() label?: string;
  @Input() dica?: string;
  @Input() placeholder = '';
  @Input() backgroundColor?: string;
  @Input() borderColor?: string;
  @Input() colorLabel?: string;
  @Input() fontLabel = '13px';
  @Input() fontWeight = '500';
  @Input() font = '12px';
  @Input() iconPrefix?: string;
  @Input() iconSuffix?: string;
  @Input() iconColor?: string;

  @Input() widthFormField = '100%';
  @Input() heightFormField = '60px';
  @Input() maxHeightFormField = '60px';
  @Input() crescerSemScroll = false;
  @Input() autoFocus = true;
  @Input() classe?: string;

  @Input() disabled = false;
  @Input() showValidationErrors = false;
  @Input() isInvalid = false;
  @Input() maxLengthFormFiles?: number;

  @Input() value = '';
  @Output() changeValue: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('internalTextarea')
  internalTextarea!: ElementRef<HTMLTextAreaElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['value'] ||
        changes['disabled'] ||
        changes['crescerSemScroll'] ||
        changes['heightFormField'] ||
        changes['maxHeightFormField']) &&
      this.internalTextarea
    ) {
      this.agendarAutoResize();
    }
  }

  ngAfterViewInit(): void {
    if (this.internalTextarea) {
      this.agendarAutoResize();
      if (!this.disabled && this.autoFocus) {
        setTimeout(() => this.internalTextarea.nativeElement.focus(), 200);
      }
    }
  }

  change(): void {
    this.changeValue.emit(this.value);
    if (this.internalTextarea) {
      this.autoResize(this.internalTextarea.nativeElement);
    }
  }

  focusInput(): void {
    if (this.internalTextarea && this.internalTextarea.nativeElement) {
      this.internalTextarea.nativeElement.focus();
    }
  }

  recalcularAltura(): void {
    this.agendarAutoResize();
  }

  inserirNoCursor(texto: string): void {
    const textarea = this.internalTextarea?.nativeElement;
    if (!textarea || this.disabled) {
      return;
    }

    const scrollLista = this.capturarScrollLista(textarea);
    const valorAtual = this.value ?? '';
    const inicio = textarea.selectionStart ?? valorAtual.length;
    const fim = textarea.selectionEnd ?? inicio;
    const novaPosicao = inicio + texto.length;
    const scrollTop = this.crescerSemScroll ? 0 : textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;

    this.value = `${valorAtual.slice(0, inicio)}${texto}${valorAtual.slice(fim)}`;
    textarea.value = this.value;
    this.changeValue.emit(this.value);
    this.autoResize(textarea);

    setTimeout(() => {
      textarea.focus({ preventScroll: true });
      textarea.setSelectionRange(novaPosicao, novaPosicao);
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
      this.restaurarScrollLista(scrollLista);
    }, 0);
  }

  getErrorMessage(): string {
    if (
      this.value &&
      this.maxLengthFormFiles &&
      this.value.length > this.maxLengthFormFiles
    ) {
      return `Máximo de ${this.maxLengthFormFiles} caracteres permitidos.`;
    }
    return 'Campo inválido.';
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    const scrollLista = this.capturarScrollLista(textarea);
    const minHeight = parseInt(this.heightFormField.replace('px', ''), 10) || 0;
    const maxHeight = this.crescerSemScroll
      ? 0
      : parseInt(this.maxHeightFormField.replace('px', ''), 10) || 0;

    textarea.style.paddingTop = '10px';
    textarea.style.paddingBottom = '10px';
    textarea.style.overflowY = this.crescerSemScroll ? 'hidden' : 'auto';
    textarea.style.maxHeight = this.crescerSemScroll ? 'none' : '';
    textarea.style.height = 'auto';

    const contentHeight = textarea.scrollHeight;
    let totalHeight = Math.max(minHeight, contentHeight);

    if (maxHeight > 0 && totalHeight > maxHeight) {
      totalHeight = maxHeight;
    }

    textarea.style.height = `${totalHeight}px`;
    if (this.crescerSemScroll) {
      textarea.scrollTop = 0;
    }
    this.restaurarScrollLista(scrollLista);
  }

  private agendarAutoResize(): void {
    setTimeout(() => {
      const textarea = this.internalTextarea?.nativeElement;
      if (!textarea) {
        return;
      }
      this.autoResize(textarea);
      requestAnimationFrame(() => this.autoResize(textarea));
    }, 0);
  }

  private capturarScrollLista(textarea: HTMLTextAreaElement): {
    elemento: HTMLElement;
    top: number;
    left: number;
  } | null {
    const elemento = textarea.closest<HTMLElement>('.lista-blocos');
    return elemento
      ? { elemento, top: elemento.scrollTop, left: elemento.scrollLeft }
      : null;
  }

  private restaurarScrollLista(
    scroll: { elemento: HTMLElement; top: number; left: number } | null
  ): void {
    if (!scroll) {
      return;
    }
    scroll.elemento.scrollTop = scroll.top;
    scroll.elemento.scrollLeft = scroll.left;
  }
}
